const FLASH_KEY = "bulk-action-flash";

let docChangeHandler: ((e: Event) => void) | null = null;
let rowsLoadedHandler: ((e: Event) => void) | null = null;
let docClickHandler: ((e: Event) => void) | null = null;

export function initCandidateBulk(): void {
  // Restore the stashed flash before the root/table guard — the reload happens
  // on a page with no rows left, which may come back without a toolbar.
  const storedFlash = window.sessionStorage.getItem(FLASH_KEY);
  if (storedFlash) {
    window.sessionStorage.removeItem(FLASH_KEY);
    const fc = document.getElementById("flash-container");
    if (fc) {
      fc.innerHTML = storedFlash;
      window.setTimeout(() => { fc.innerHTML = ""; }, 5000);
    }
  }

  const root = document.querySelector<HTMLElement>("[data-candidate-bulk]");
  if (!root) return;
  const tables = (): HTMLElement[] =>
    Array.from(document.querySelectorAll<HTMLElement>(".cand-table"));

  const tab = root.dataset.tab || "";
  const bulkbar = document.getElementById("bulkbar") as HTMLElement | null;

  const countEl = document.getElementById("bulk-count");
  const bulkAll = document.getElementById("bulk-all");

  const reviewModeBtn = document.querySelector<HTMLButtonElement>("[data-review-mode-open]");

  const queryAll = <T extends Element>(sel: string): T[] => {
    const found: T[] = [];
    tables().forEach((t) => t.querySelectorAll<T>(sel).forEach((el) => found.push(el)));
    return found;
  };

  const checkboxes = (): HTMLInputElement[] => queryAll<HTMLInputElement>("input.bulk-check");

  const rowOf = (cb: HTMLInputElement): HTMLElement | null => cb.closest("tr");

  const isVisible = (cb: HTMLInputElement): boolean => {
    const tr = rowOf(cb);
    return !!tr && tr.style.display !== "none";
  };

  const selectedIds = (): string[] =>
    checkboxes().filter((cb) => cb.checked && isVisible(cb)).map((cb) => cb.dataset.id || "").filter(Boolean);

  const csrf = (): string =>
    (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content || "";

  function updateBar(): void {
    if (!bulkbar) return;
    const ids = selectedIds();
    if (countEl) countEl.textContent = String(ids.length);
    bulkbar!.style.display = ids.length ? "flex" : "none";
    const visibleBoxes = checkboxes().filter(isVisible);
    const allOn = visibleBoxes.length > 0 && ids.length === visibleBoxes.length;
    if (bulkAll) bulkAll.classList.toggle("on", allOn);
    tables().forEach((t) => {
      const headerAll = t.querySelector<HTMLInputElement>(".bulk-check-all");
      if (!headerAll) return;
      const boxes = Array.from(t.querySelectorAll<HTMLInputElement>("input.bulk-check")).filter(isVisible);
      const on = boxes.filter((cb) => cb.checked).length;
      headerAll.checked = boxes.length > 0 && on === boxes.length;
      headerAll.indeterminate = on > 0 && on < boxes.length;
    });
  }

  // The flash markup the endpoint rendered, dropped straight into the layout's
  // container (nothing reloads, so there is nothing to stash it through).
  function showFlash(html: string): void {
    const $fc = $("#flash-container");
    if (!$fc.length) return;
    $fc.html(html);
    window.setTimeout(() => { $fc.html(""); }, 5000);
  }

  // A candidate can own more than one <tr>: accepted_no_email hangs a status
  // picker row off the end. Take the row plus everything trailing it up to the
  // next candidate row.
  function removeRows(ids: Array<string | number>): void {
    ids.forEach((id) => {
      const cb = queryAll<HTMLInputElement>(`input.bulk-check[data-id="${String(id)}"]`)[0];
      const tr = cb?.closest("tr");
      if (!tr) return;
      let next = tr.nextElementSibling;
      while (next && next.tagName === "TR" && !next.hasAttribute("data-queued-row")) {
        const after = next.nextElementSibling;
        next.remove();
        next = after;
      }
      tr.remove();
    });
  }

  // Review mode walks the whole filtered queue, so with nothing left in it the
  // panel would open straight onto the "everything reviewed" screen.
  function syncReviewModeButton(remaining: number): void {
    if (!reviewModeBtn) return;
    reviewModeBtn.disabled = remaining === 0;
    reviewModeBtn.title = remaining === 0 ? "No candidates match the current filters" : "";
  }

  // Tab badges + the toolbar total come from the server (the page's filter
  // params ride along on the request, so they match what is on screen); the
  // pager range is restated from the rows actually left on this page.
  function updateCounters(counts?: Record<string, number>): void {
    const rows = checkboxes().length;
    const hasCount = !!counts && Object.prototype.hasOwnProperty.call(counts, tab);

    if (counts) {
      $("[data-tab-badge]").each(function () {
        const key = this.getAttribute("data-tab-badge") || "";
        if (Object.prototype.hasOwnProperty.call(counts, key)) $(this).text(String(counts[key]));
      });

      const $total = $(".cand-count");
      if ($total.length && hasCount) {
        const n = counts[tab];
        const sing = $total.attr("data-noun-singular") || "candidate";
        const plur = $total.attr("data-noun-plural") || "candidates";
        $total.text(`${n} ${n === 1 ? sing : plur}`);
      }

      const $info = $(".pager-info");
      if ($info.length && hasCount) {
        const offset = Number($info.attr("data-offset") || 0);
        $info.html(`Showing <b>${rows ? offset + 1 : 0}&ndash;${offset + rows}</b> of ${counts[tab]}`);
      }
    }

    // "Showing N of M contacted" under the contacted toolbar. That tab carries no
    // data-tab, so it never has a server count to read — N is the rows on this page.
    const contactedCountEl = document.getElementById("contacted-count");
    if (contactedCountEl) contactedCountEl.textContent = String(rows);

    // The queue spans every page, so trust the server count where there is one.
    syncReviewModeButton(hasCount ? Number(counts![tab]) : rows);
    updateBar();
  }

  function needsReload(): boolean {
    return checkboxes().length === 0;
  }

  // "later" is a reject carrying the "maybe later" reason — same endpoint.
  type BulkKind = "accept" | "reject" | "later" | "sequence";
  const MAYBE_LATER = "maybe later";

  async function runBulk(kind: BulkKind, extra?: Record<string, unknown>): Promise<void> {
    const ids = selectedIds();
    if (!ids.length) return;
    if (kind === "reject" && !window.confirm(`Reject ${ids.length} candidate(s)?`)) return;
    if (kind === "later" && !window.confirm(`Move ${ids.length} candidate(s) to maybe later?`)) return;

    const base =
      kind === "accept" ? bulkbar!.dataset.acceptUrl :
      kind === "sequence" ? bulkbar!.dataset.sequenceUrl :
      bulkbar!.dataset.rejectUrl;
    if (!base) return;

    // Carry the page's filter params so the badge counts the endpoint sends
    // back are narrowed the same way the rows on screen are.
    const url = base + window.location.search;

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf(), Accept: "application/json" },
        body: JSON.stringify({ bots_prospect_ids: ids, current_tab: tab, ...(extra || {}) }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      // Only the rows the write actually moved off this tab — a candidate the
      // action left in place (or that the endpoint skipped) keeps its row.
      if (Array.isArray(data.removed_ids)) removeRows(data.removed_ids);

      if (data.reload || needsReload()) {
        if (data.flash) window.sessionStorage.setItem(FLASH_KEY, data.flash);
        window.location.reload();
        return;
      }

      updateCounters(data.counts);
      if (data.flash) showFlash(data.flash);
    } catch (e) {
      window.alert("Bulk action failed. Please try again.");
    }
  }

  // --- selection ---
  // Delegated from the document so rows added by a later async company fetch are wired.
  if (docChangeHandler) document.removeEventListener("change", docChangeHandler);
  docChangeHandler = (e: Event) => {
    const t = e.target as HTMLElement;
    if (!t || !t.closest?.(".cand-table")) return;
    if (t.classList.contains("bulk-check")) updateBar();
    if (t.classList.contains("bulk-check-all")) toggleAll(t.closest<HTMLElement>(".cand-table"));
  };
  document.addEventListener("change", docChangeHandler);

  // Shared select-all: all on → clear all; any off → select all. The bulkbar's box
  // spans the page; a table header's box is limited to that table (one company).
  function toggleAll(scope?: HTMLElement | null): void {
    const pool = scope
      ? Array.from(scope.querySelectorAll<HTMLInputElement>("input.bulk-check"))
      : checkboxes();
    const visibleBoxes = pool.filter(isVisible);
    const allOn = visibleBoxes.length > 0 && visibleBoxes.every((cb) => cb.checked);
    const turnOn = !allOn;
    visibleBoxes.forEach((cb) => { cb.checked = turnOn; });
    updateBar();
  }

  bulkAll?.addEventListener("click", () => toggleAll());

  // A company block's rows arrive after init — re-tally the selection over them.
  if (rowsLoadedHandler) document.removeEventListener("candidates:rows-loaded", rowsLoadedHandler);
  rowsLoadedHandler = () => updateBar();
  document.addEventListener("candidates:rows-loaded", rowsLoadedHandler);

  document.getElementById("bulk-clear")?.addEventListener("click", () => {
    checkboxes().forEach((cb) => { cb.checked = false; });
    updateBar();
  });

  // --- Add to sequence: picker menu + confirm modal ---
  const seqBtn = bulkbar?.querySelector<HTMLElement>('[data-bulk="sequence"]') ?? null;
  const seqMenu = document.getElementById("seq-menu");
  const rejectMenu = document.getElementById("reject-menu");
  const seqConfirm = document.getElementById("seq-confirm");
  let pendingSequenceId: string | null = null;

  // No valid sequences to pin to → drop the action entirely.
  if (seqBtn && !seqMenu?.querySelector(".seq-opt")) seqBtn.style.display = "none";

  const closeConfirm = (): void => { seqConfirm?.classList.remove("open"); };

  if (docClickHandler) document.removeEventListener("click", docClickHandler);
  docClickHandler = (e: Event) => {
    const target = e.target as HTMLElement;
    const opt = target.closest<HTMLElement>("#reject-menu .rej-opt");
    if (opt && rejectMenu) {
      // Pick a reject reason (or "Other / no reason") → reject the selection with it.
      e.stopPropagation();
      rejectMenu.classList.remove("open");
      const reason = opt.dataset.reason || "";
      runBulk("reject", reason ? { reject_reason: reason } : undefined);
      return;
    }
    const el = target.closest<HTMLElement>("#bulkbar .bulk-act");
    if (!el) {
      if (!target.closest(".seq-menu")) {
        seqMenu?.classList.remove("open");
        rejectMenu?.classList.remove("open");
      }
      return;
    }
    const kind = el.dataset.bulk as BulkKind;
    // Reject and Add to sequence both open a picker; only one at a time.
    if (kind === "sequence" || kind === "reject") {
      if (target.closest(".seq-menu")) return; // menu clicks handled below
      if (!selectedIds().length) return;
      const menu = kind === "sequence" ? seqMenu : rejectMenu;
      const other = kind === "sequence" ? rejectMenu : seqMenu;
      other?.classList.remove("open");
      menu?.classList.toggle("open");
      return;
    }
    if (kind === "later") {
      runBulk(kind, { reject_reason: MAYBE_LATER });
      return;
    }
    runBulk(kind);
  };
  document.addEventListener("click", docClickHandler);

  // Pick a sequence → confirmation step before queueing.
  seqMenu?.querySelectorAll<HTMLElement>(".seq-opt").forEach((opt) => {
    opt.addEventListener("click", (e) => {
      e.stopPropagation();
      seqMenu.classList.remove("open");
      const n = selectedIds().length;
      if (!n) return;
      pendingSequenceId = opt.dataset.seqId ?? null;
      const cfmCount = document.getElementById("cfm-count");
      const cfmNoun = document.getElementById("cfm-noun");
      const cfmSeq = document.getElementById("cfm-seq");
      if (cfmCount) cfmCount.textContent = String(n);
      if (cfmNoun) {
        const sing = seqConfirm?.dataset.nounSingular || "candidate";
        const plur = seqConfirm?.dataset.nounPlural || "candidates";
        cfmNoun.textContent = n === 1 ? sing : plur;
      }
      if (cfmSeq) cfmSeq.textContent = opt.dataset.seqName ?? "";
      seqConfirm?.classList.add("open");
    });
  });

  document.getElementById("cfm-cancel")?.addEventListener("click", closeConfirm);
  document.getElementById("cfm-accept")?.addEventListener("click", () => {
    closeConfirm();
    if (pendingSequenceId) runBulk("sequence", { sequence_id: pendingSequenceId });
  });
  seqConfirm?.addEventListener("click", (e) => { if (e.target === seqConfirm) closeConfirm(); });
  document.addEventListener("keydown", (e) => { if (e.key === "Escape") closeConfirm(); });

  updateBar();
}
